import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { useProjectStages, useStageSetsOfKind } from '@/features/projects/api'
import { useMoveProject } from '@/features/projects/reorder'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { resolveStageInSameSet } from '@/shared/api/adapt'
import { KanbanBoard, type KanbanColumnSpec } from '@/shared/components/dnd'
import { useT } from '@/shared/i18n'
import { getDaysSince } from '@/shared/lib/due-date'
import type { ProjectSummary, Stage, StageCategory } from '@/shared/types/domain'

import { OngoingSwimlane, SWIMLANE_REJECT_KEY } from './components/OngoingSwimlane'
import { ProjectCard } from './components/ProjectCard'

/**
 * El orden de las columnas es fijo y no es el de la tabla `stages`: es el orden
 * en que se lee el trabajo. "Pausado" va último porque es una excepción, no una
 * etapa del flujo, y ponerlo primero haría que lo primero que se mire cada
 * mañana sea lo que está detenido.
 */
const COLUMN_ORDER: readonly StageCategory[] = ['backlog', 'active', 'review', 'done', 'paused']

/** Cuántos días de "Culminado" se muestran antes de pedir "ver todos". */
const DONE_WINDOW_DAYS = 30

/**
 * Recorte de "Culminado" a los últimos 30 días.
 *
 * `ProjectListItem` **sí trae `actual_end_date`**, así que el recorte es real:
 * lo que se cerró hace más de 30 días se guarda detrás de "Ver todos". Una
 * columna de terminados que crece para siempre deja de ser información y pasa a
 * ser archivo.
 *
 * Los "días desde" salen de `getDaysSince`, que es la MISMA fuente de "hoy" y
 * de timezone que el badge de vencimiento y que los atajos del selector de
 * fecha. Antes esto armaba el día de hoy por su cuenta con `Date.UTC` sobre
 * componentes locales: la misma pregunta contestada con otra regla.
 *
 * Sin fecha de cierre **no se esconde**: un proyecto en `done` al que nadie le
 * puso fecha no es viejo, es un dato que falta, y esconderlo sería afirmar algo
 * que no sabemos.
 */
function isRecentlyDone(project: ProjectSummary): boolean {
  const age = getDaysSince(project.actual_end_date)
  return age === null || age <= DONE_WINDOW_DAYS
}

export interface BoardViewProps {
  /**
   * Los proyectos YA filtrados por la barra del portafolio, en orden de rank.
   *
   * El tablero no pide los suyos: es la misma lista que la tabla y las
   * tarjetas, agrupada por fase. Cuando tenía su propia query, "el tablero de
   * Nube Andina" no existía y las dos vistas podían mostrar conjuntos distintos
   * del mismo portafolio.
   */
  projects: readonly ProjectSummary[]
  isPending: boolean
  isError: boolean
  error: unknown
  onRetry: () => void
}

/**
 * El kanban de proyectos: una columna por categoría de fase, más la swimlane de
 * permanentes.
 *
 * Es una VISTA, no una pantalla: no tiene `PageHeader` ni pide datos. La
 * cabecera, los KPIs y los filtros son los del portafolio.
 */
export function BoardView({ projects, isPending, isError, error, onRetry }: BoardViewProps) {
  const t = useT()
  const stages = useProjectStages()
  // Los sets CRUDOS, además de las fases aplanadas: las columnas se dibujan con
  // las aplanadas (que de-duplican por `key` y se quedan con las del sistema),
  // pero mover necesita el `stage_id` del set de la empresa del proyecto.
  const stageSets = useStageSetsOfKind('project')
  const move = useMoveProject()
  const [showAllDone, setShowAllDone] = useState(false)

  const ordered = useMemo(() => [...projects], [projects])
  const ongoing = useMemo(() => ordered.filter((project) => project.is_ongoing), [ordered])

  /** El ordinal 1..X sale del orden global, no de la posición en la columna. */
  const positions = useMemo(() => {
    const map = new Map<string, number>()
    ordered
      .filter((project) => !project.is_ongoing)
      .forEach((project, index) => map.set(project.id, index + 1))
    return map
  }, [ordered])

  const columns = useMemo<KanbanColumnSpec<ProjectSummary>[]>(() => {
    const byCategory = new Map<StageCategory, Stage>()
    for (const stage of stages.data ?? []) {
      if (!byCategory.has(stage.category)) byCategory.set(stage.category, stage)
    }

    return COLUMN_ORDER.flatMap((category) => {
      const stage = byCategory.get(category)
      if (!stage) return []

      const all = ordered.filter(
        (project) => !project.is_ongoing && project.stage.category === category,
      )
      const items = category === 'done' && !showAllDone ? all.filter(isRecentlyDone) : all
      const hidden = all.length - items.length

      return [
        {
          id: stage.id,
          title: stage.name,
          category,
          items,
          // El cartel solo aparece si el recorte efectivamente recortó algo:
          // anunciar "Últimos 30 días" sobre la lista completa sería un cartel
          // que miente sobre lo que el usuario está viendo.
          hint:
            category === 'done' && !showAllDone && hidden > 0
              ? t('board.done.window', { count: DONE_WINDOW_DAYS })
              : undefined,
          footer:
            category === 'done' && (hidden > 0 || showAllDone) ? (
              <button
                type="button"
                onClick={() => setShowAllDone((value) => !value)}
                className="px-1 py-1 text-left text-2xs text-text-2 underline-offset-2 hover:text-signal hover:underline"
              >
                {showAllDone
                  ? t('board.done.showRecent', { count: DONE_WINDOW_DAYS })
                  : t('board.done.showAll', { count: hidden })}
              </button>
            ) : undefined,
        } satisfies KanbanColumnSpec<ProjectSummary>,
      ]
    })
  }, [ordered, showAllDone, stages.data, t])

  if (isPending || stages.isPending) return <SkeletonRows rows={6} />
  if (isError) return <ErrorState error={error} onRetry={onRetry} />
  if (ordered.length === 0) {
    return <EmptyState title={t('board.empty.title')} description={t('board.empty.description')} />
  }

  return (
    <div className="flex flex-col gap-2">
      {/*
        Como se usa el tablero, ahora que no tiene cabecera propia.
        Era la `description` del `PageHeader` de `/board` y es la unica linea
        que dice que las tarjetas se arrastran y que el orden dentro de una
        columna es el de Prioridades. Sin ella el gesto queda por descubrir.
      */}
      <p className="text-2xs text-text-3">{t('board.description')}</p>

      <KanbanBoard
        columns={columns}
        ariaLabel={t('board.ariaLabel')}
        getLabel={(project) => project.name}
        header={<OngoingSwimlane projects={ongoing} />}
        onRejectedDrop={(_zoneId, reason) => toast.info(reason ?? t(SWIMLANE_REJECT_KEY))}
        onMove={({ itemId, beforeId, afterId, toColumnId, changedColumn }) => {
          const moved = ordered.find((project) => project.id === itemId)

          // La columna destino, traducida al juego de columnas de ESE proyecto.
          // Sin traducir viajaba el `stage_id` del set del sistema y el backend
          // contestaba 409 para cualquier proyecto.
          const stage =
            changedColumn && moved
              ? (resolveStageInSameSet(stageSets.data ?? [], moved.stage.id, toColumnId) ??
                undefined)
              : undefined

          if (changedColumn && !stage) {
            toast.error(t('board.moveError'))
            return
          }

          move.mutate({ id: itemId, beforeId, afterId, stage })
        }}
        renderCard={(project, state) => (
          <ProjectCard
            project={project}
            position={positions.get(project.id) ?? null}
            handle={state.handle}
            isDragging={state.isDragging}
          />
        )}
      />
    </div>
  )
}
